<script lang="ts">
import { computed, defineComponent, inject, PropType, ref } from "vue";
import Steps from "@/components/Steps.vue";
import formatUtils from "@/businesslogic/utils/formatUtils";
import Abschluss from "@/components/abschluss/Abschluss.vue";
import { NavvItem } from "@/model/NavItem";
import IconSprite from "@/components/IconSprite.vue";
import Infotext from "@/components/Infotext.vue";
import dataUtils from "@/businesslogic/utils/dataUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "AbschlussHG",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    Abschluss,
    Steps,
    IconSprite,
    Infotext,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
  },
  setup(props) {
    const piniaStore = useFormStore();

    const { gt } = dataUtils();

    const gebaeudeTyp = computed(() => {
      if (piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false) {
        switch (piniaStore.state.gebaeudedaten.gebaeudetyp) {
          case "0001":
            return "Mehrfamilienhaus";
          case "0002":
            return "Einfamilienhaus";
          case "0003":
            return "Vermietete Eigentumswohnung";
          case "0004":
            return "WEG Gemeinschafts-, Sonder- und Teileigentum";
          case "0005":
            return "WEG Gemeinschaftseigentum";
          default:
            return "";
        }
      }
      return "";
    });

    const wohnungsAnzahl = computed(() => {
      if (
        piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== "0002"
      ) {
        return ", " + piniaStore.state.gebaeudedaten.anzahlWohnungen + " Wohnungen";
      }
      return "";
    });

    const anzahlGewerbe = computed(() => {
      if (
        piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== "0002" &&
        piniaStore.state.gebaeudedaten.gebaeudetyp !== "0003" &&
        gt(piniaStore.state.gebaeudedaten.gewerbeAnzahl, 0)
      ) {
        return ", " + piniaStore.state.gebaeudedaten.gewerbeAnzahl + " Gewerbebetriebe";
      }
      return "";
    });

    const leerstandProzent = computed(() => {
      if (
        piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false &&
        piniaStore.state.gebaeudedaten.leerstand !== 0
      ) {
        return ", " + piniaStore.state.gebaeudedaten.leerstand + " % Leerstand";
      }
      return "";
    });

    const fehler = ref({} as any);

    const { formatCurrency } = formatUtils();
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");

    const antragGemaessAngabenTextMakler = computed(() => {
      return (
        "Ja, der Antrag wurde gemäß meinen Angaben erstellt. Auf eine Beratung habe" +
        "ich verzichtet und damit auf den Schadenersatzanspruch nach § 6 Abs. 5" +
        "VVG."
      );
    });

    const gebaeudeInfoText = computed(() => {
      if (piniaStore.state.gebaeudedaten.gewerbeVorhanden) {
        return (
          "Gebäude mit folgender Nutzung können nicht versichert werden: " +
          "Gebäude, die zum Abriss bestimmt oder gegen den Willen " +
          "des Eigentümers besetzt sind;  Wohnheime ohne abgeschlossene Wohnungen.<br/><br/>" +
          "Die GEV versichert keine Gebäude, in denen " +
          "grundwassergefährdende Stoffe wie beispielsweise " +
          "Farben, Lacke, Treibstoffe etc. über den " +
          "haushaltsüblichen Umfang hinaus gelagert werden." +
          "Beispiele: Chemische Reinigungen, Tankstellen, " +
          "Malereibetriebe, Lackierereien, Entsorgungs- und Recyclingbetriebe, " +
          "Herstellerbetriebe von Farben und Lacken.<br/><br/>" +
          "Die GEV versichert keine Gebäude in denen sich z. B." +
          "folgende Gewerbearten befinden: Stundenhotels," +
          "Bordelle, Discotheken, Nachtclubs und Bars."
        );
      }
      return (
        "Gebäude mit folgender Nutzung können nicht versichert werden: " +
        "Gebäude, die zum Abriss bestimmt oder gegen den Willen " +
        "des Eigentümers besetzt sind;  Wohnheime ohne abgeschlossene Wohnungen."
      );
    });

    const antragGemaessAngabenText = computed(() => {
      if (piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false) {
        if (piniaStore.state.gebaeudedaten.gewerbeVorhanden === false)
          return (
            "Ja, der Antrag wurde gemäß meinen Angaben erstellt. " +
            "Hiermit bestätige ich, dass sich nur Gebäude mit versicherbarer " +
            "Nutzung an der Risikoanschrift befinden. Auf eine Beratung habe ich verzichtet und " +
            "damit auf den Schadenersatzanspruch nach §6. Abs. 5 VVG."
          );
        else {
          return (
            "Ja, der Antrag wurde gemäß meinen Angaben erstellt. " +
            "Hiermit bestätige ich, dass sich nur Gebäude mit versicherbarer " +
            "Nutzung und nur zulässiges Gewerbe an der Risikoanschrift befinden. " +
            "Auf eine Beratung habe ich verzichtet und " +
            "damit auf den Schadenersatzanspruch nach §6. Abs. 5 VVG."
          );
        }
      }
      return (
        "Ja, der Antrag wurde gemäß meinen Angaben erstellt. Bei meinem Grundstück " +
        "handelt es sich nicht um ein Fluss-, See- oder Meergrundstück bzw. nicht um ein " +
        "Grundstück mit überwiegend Energieerzeugungsanlagen. Auf eine Beratung habe ich " +
        "verzichtet und damit auf den Schadenersatzanspruch nach §6. Abs. 5 VVG."
      );
    });

    return {
      props,
      piniaStore,
      fehler,
      formatCurrency,
      gebaeudeTyp,
      wohnungsAnzahl,
      anzahlGewerbe,
      leerstandProzent,
      antragGemaessAngabenText,
      antragGemaessAngabenTextMakler,
      wechsleSichtbarkeit,
      gebaeudeInfoText,
    };
  },
});
</script>

<template>
  <steps v-bind:items="navItems" :active="3" @goto-page="$emit('goto-page', $event)" />

  <abschluss
    produkt="hg"
    :zeige-versichertes-objekt="true"
    :zeige-tarif="true"
    :zeige-objekt-anschrift="true"
    :zeige-selbstbeteiligung="false"
    :zeige-anzahl-vorschaeden="true"
    :zeige-eigentumsverhaeltnisse="false"
    @next-page="$emit('next-page', $event)"
    @goto-page="$emit('goto-page', $event)"
  >
    <template v-slot:versichertes-objekt>
      <span v-if="piniaStore.state.gebaeudedaten.unbebautesGruendstueck === true">
        <strong>
          Unbebautes Grundstück,
          {{ piniaStore.state.gebaeudedaten.flaecheUnbebautesGrundstueck }}m<sup>2</sup>
        </strong>
      </span>
      <span
        v-if="
          piniaStore.state.gebaeudedaten.gebaeudetyp === '0006' &&
          piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false
        "
      >
        <strong>
          {{ piniaStore.state.gebaeudedaten.anzahlStellplaetzeGaragen }}
          {{
            piniaStore.state.gebaeudedaten.anzahlStellplaetzeGaragen === 1
              ? "Garage oder Stellplatz"
              : "Garagen und/oder Stellplätze"
          }}
        </strong>
      </span>
      <span v-else>
        <strong> {{ gebaeudeTyp }}{{ wohnungsAnzahl }}{{ anzahlGewerbe }}{{ leerstandProzent }} </strong>
      </span>
    </template>

    <template v-if="piniaStore.state.isMakler" v-slot:angaben-bestaetigen>
      {{ antragGemaessAngabenTextMakler }}
    </template>

    <template v-else v-slot:angaben-bestaetigen>
      {{ antragGemaessAngabenText }}
      <span
        v-if="piniaStore.state.gebaeudedaten.unbebautesGruendstueck === false"
        class="c-notice-icon"
        @click="wechsleSichtbarkeit('gewerbeHilfe')"
      >
        <icon-sprite icon-name="icon-info-i" />
      </span>
      <infotext name="gewerbeHilfe" :text="gebaeudeInfoText" />
    </template>

    <template v-if="piniaStore.state.isMakler" v-slot:gewerbe-zulaessig>
      Hiermit bestätige ich, dass sich nur zulässiges Gewerbe an der Risikoanschrift befindet.
    </template>
  </abschluss>
</template>
